/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/**
 * Guest Add to Cart: offer OTP login or let the customer continue as a guest.
 * The original product form payload is preserved so variant and add-on selections
 * are added after either a successful login or the Skip login action.
 */
(() => {
    const STORAGE_KEY = 'isha_pending_cart_add_v1';
    const MODAL_ID = 'isha-cart-login-choice';

    const isLoggedIn = () => Boolean(
        window.simplyOtp?.customer ||
        window.meta?.page?.customerId ||
        window.Shopify?.customer
    );

    const getPending = () => {
        try {
            return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
        } catch (error) {
            sessionStorage.removeItem(STORAGE_KEY);
            return null;
        }
    };

    const getSelectedAddonProducts = (form) => {
        const addons = [];
        const selected = form.querySelectorAll(
            '[gpo-data-variant-id]:checked, option[gpo-data-variant-id]:checked'
        );

        selected.forEach((field) => {
            const fieldName = field.getAttribute('data-field-name') || field.name || '';
            const defaultQuantity = Number(field.getAttribute('data-quantity')) || 1;
            const variantValues = (field.getAttribute('gpo-data-variant-id') || '').split(',');

            variantValues.forEach((variantValue) => {
                const [idValue, quantityValue] = variantValue.split('|').map((value) => value.trim());
                const id = Number(idValue);

                if (Number.isFinite(id) && id > 0) {
                    addons.push({
                        id,
                        quantity: Number(quantityValue) || defaultQuantity,
                        fieldName
                    });
                }
            });
        });

        return addons;
    };

    const savePending = (form) => {
        const fields = [];

        for (const [name, value] of new FormData(form).entries()) {
            if (typeof value === 'string') {
                fields.push([name, value]);
            }
        }

        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
            fields,
            addonProducts: getSelectedAddonProducts(form),
            createdAt: Date.now()
        }));
    };

    const addPendingToCart = async () => {
        const pending = getPending();

        if (!pending?.fields?.length) {
            return;
        }

        // Do not add an abandoned item during a much later visit.
        if (Date.now() - pending.createdAt > 30 * 60 * 1000) {
            sessionStorage.removeItem(STORAGE_KEY);
            return;
        }

        const addons = Array.isArray(pending.addonProducts) ? pending.addonProducts : [];
        let body;
        let headers = { Accept: 'application/json' };

        if (addons.length) {
            const properties = {};
            let variantId = null;
            let quantity = 1;

            pending.fields.forEach(([name, value]) => {
                if (name === 'id') {
                    variantId = Number(value);
                    return;
                }

                if (name === 'quantity') {
                    quantity = Number(value) || 1;
                    return;
                }

                const propertyMatch = name.match(/^properties\[([^\]]+)\](?:\[\])?$/);
                if (propertyMatch && value) {
                    properties[propertyMatch[1]] = value;
                }
            });

            if (!variantId) {
                throw new Error('Unable to identify the selected product variant.');
            }

            delete properties._gpo_options;
            delete properties._gpo_addon_products;

            const productGroup = String(Date.now());
            properties._gpo_product_group = productGroup;

            const items = addons.map((addon) => ({
                id: addon.id,
                quantity: addon.quantity,
                properties: {
                    _gpo_parent_product_group: productGroup,
                    _gpo_field_name: addon.fieldName
                }
            }));

            items.push({
                id: variantId,
                quantity,
                properties
            });

            body = JSON.stringify({
                items,
                sections_url: window.location.pathname
            });
            headers = {
                Accept: 'application/json',
                'Content-Type': 'application/json'
            };
        } else {
            body = new FormData();
            pending.fields.forEach(([name, value]) => body.append(name, value));
        }

        const response = await fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
            method: 'POST',
            headers,
            body,
            credentials: 'same-origin',
            keepalive: true
        });

        if (!response.ok) {
            throw new Error('Unable to add the selected item to cart.');
        }

        sessionStorage.removeItem(STORAGE_KEY);

        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
            bubbles: true
        }));

        window.setTimeout(() => {
            const miniCart = document.getElementById('mini-cart');
            if (miniCart) {
                miniCart.open = true;
            }
        }, 250);
    };

    const closeChoiceModal = () => {
        document.getElementById(MODAL_ID)?.remove();
    };

    const showChoiceModal = () => {
        closeChoiceModal();

        const overlay = document.createElement('div');
        overlay.id = MODAL_ID;
        overlay.style.cssText = [
            'position:fixed',
            'inset:0',
            'z-index:2147483646',
            'display:flex',
            'align-items:center',
            'justify-content:center',
            'padding:20px',
            'background:rgba(0,0,0,.58)'
        ].join(';');

        overlay.innerHTML = `
            <div role="dialog" aria-modal="true" aria-labelledby="isha-cart-login-title"
                 style="position:relative;width:min(420px,100%);padding:28px;background:#fff;border-radius:14px;box-shadow:0 20px 60px rgba(0,0,0,.3);text-align:center;color:#111">
                <button type="button" data-isha-close aria-label="Close"
                        style="position:absolute;right:12px;top:8px;border:0;background:transparent;font-size:28px;line-height:1;cursor:pointer">&times;</button>
                <h2 id="isha-cart-login-title" style="margin:0 0 22px;font-size:22px">Enter Mobile Number to get Discount Code</h2>
                <button type="button" data-isha-login
                        style="width:100%;padding:13px 16px;border:1px solid #111;border-radius:6px;background:#111;color:#fff;font:inherit;font-weight:700;cursor:pointer">Login with OTP</button>
                <button type="button" data-isha-skip
                        style="width:100%;margin-top:10px;padding:13px 16px;border:1px solid #111;border-radius:6px;background:#fff;color:#111;font:inherit;font-weight:700;cursor:pointer">Skip login &amp; add to cart</button>
            </div>
        `;

        overlay.querySelector('[data-isha-close]').addEventListener('click', closeChoiceModal);

        overlay.querySelector('[data-isha-login]').addEventListener('click', () => {
            closeChoiceModal();

            const loginLink = document.querySelector(
                'a[href="/account"], a[href*="/account/login"]'
            );

            if (loginLink) {
                loginLink.click();
            } else {
                window.location.href = '/account/login?return_url=' +
                    encodeURIComponent(window.location.pathname + window.location.search);
            }
        });

        overlay.querySelector('[data-isha-skip]').addEventListener('click', async (event) => {
            const button = event.currentTarget;
            button.disabled = true;
            button.textContent = 'Adding to cart…';

            try {
                await addPendingToCart();
                closeChoiceModal();
            } catch (error) {
                button.disabled = false;
                button.textContent = 'Skip login & add to cart';
                window.alert(error.message);
            }
        });

        document.body.appendChild(overlay);
    };

    document.addEventListener('click', (event) => {
        const button = event.target.closest(
            '[data-product-add-to-cart-button], button[name="add"], .ProductForm__AddToCart'
        );

        const form = button?.closest('form[action*="/cart/add"]');

        if (!form || isLoggedIn()) {
            return;
        }

        if (typeof form.reportValidity === 'function' && !form.reportValidity()) {
            return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();

        savePending(form);
        button.disabled = true;

        addPendingToCart().catch((error) => {
            console.error('[ISHA guest cart]', error);
            window.alert(error.message);
        }).finally(() => {
            button.disabled = false;
        });
    }, true);

    document.body.addEventListener('sotp.login', () => {
        addPendingToCart().catch((error) => {
            console.error('[ISHA guest cart]', error);
        });
    });

    // Fallback for login providers that finish with a page reload.
    window.addEventListener('load', () => {
        if (isLoggedIn() && getPending()) {
            addPendingToCart().catch((error) => {
                console.error('[ISHA guest cart]', error);
            });
        }
    });
})();
